import React from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Play, ShieldCheck, X } from "lucide-react";
import { STATUS_META } from "./constants";

/**
 * Read-only list of correction rows for the current testing cycle.
 * Row-level status transition buttons (Start / Mark Corrected / Verify /
 * Reopen) are gated by `isProduction` and `isTesting` — role logic stays
 * at the panel level, this component is a pure renderer + callback bag.
 */
export default function CorrectionsList({
  items, loading, isProduction, isTesting, busy,
  onStart, onMarkCorrected, onVerify, onReopen,
}) {
  if (loading) return <div className="text-xs text-[#6C757D]">Loading…</div>;
  if (!items.length) return null;
  return (
    <div className="space-y-2">
      {items.map((c) => {
        const meta = STATUS_META[c.status] || STATUS_META.open;
        return (
          <div key={c.id} className="bg-white border border-[#E9ECEF] rounded-sm p-3"
               data-testid={`correction-row-${c.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.14em] font-bold ${meta.tone}`}>{meta.label}</span>
                  {c.priority !== "normal" && (
                    <span className={`px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-[0.14em] font-bold ${c.priority === "critical" ? "bg-red-600 text-white" : "bg-orange-500 text-white"}`}>
                      {c.priority}
                    </span>
                  )}
                  <span className="text-[10px] text-[#6C757D] uppercase tracking-[0.12em]">
                    {(c.category || "other").replace("_", " ")}
                  </span>
                </div>
                <div className="text-sm font-semibold text-[#0B132B] mt-1">{c.issue_title}</div>
                {c.description && <div className="text-xs text-[#6C757D] mt-1">{c.description}</div>}
                {(c.notes || []).length > 0 && (
                  <div className="text-[11px] text-[#6C757D] italic mt-1">
                    {c.notes[c.notes.length - 1].by}: "{c.notes[c.notes.length - 1].text}"
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {isProduction && c.status === "open" && (
                  <Button size="sm" variant="outline" onClick={() => onStart(c.id)}
                          disabled={busy} className="h-7 rounded-sm text-[10px]"
                          data-testid={`btn-start-${c.id}`}>
                    <Play size={10} className="mr-1" /> Start
                  </Button>
                )}
                {isProduction && (c.status === "in_progress" || c.status === "reopened") && (
                  <Button size="sm" onClick={() => onMarkCorrected(c.id)}
                          disabled={busy} className="h-7 rounded-sm text-[10px] bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid={`btn-corrected-${c.id}`}>
                    <CheckCircle2 size={10} className="mr-1" /> Mark Corrected
                  </Button>
                )}
                {isTesting && c.status === "corrected" && (
                  <>
                    <Button size="sm" onClick={() => onVerify(c.id)} disabled={busy}
                            className="h-7 rounded-sm text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid={`btn-verify-${c.id}`}>
                      <ShieldCheck size={10} className="mr-1" /> Verify
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onReopen(c.id)}
                            disabled={busy} className="h-7 rounded-sm text-[10px] border-red-300 text-red-700"
                            data-testid={`btn-reopen-${c.id}`}>
                      <X size={10} className="mr-1" /> Reopen
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
